import { BlockComponentFactory } from 'shared/express-interface/blocks-core/BlockFactory'
import { isEmpty } from 'lodash'
import { ViolationType } from 'shared/violations/types'
import { TaskBlock } from 'shared/express-interface/blocks-core/TaskBlock'
import { EditorContainer } from 'Tasks/ConversationTaskv2/UI/EditorContainer/EditorContainer'
import { useMemo, useState } from 'react'
import { FileToModelValueType } from 'Types/Attribute.types'
import { useTaskContext } from 'Contexts/TaskContext'

export type ConversationsInputBlockProps = {
    inputEditorBlockElement: TaskBlock<Record<string, unknown>>
    editIndex: () => number
    setEditIndex: (index: number) => void
    userMessage: () => string
    submitTurn: (editIndex: number) => void
    isTurnExhausted: () => boolean
    isLoading: boolean
    isResponseStreaming: () => boolean
    setIsLoading: (isLoading: boolean) => void
    errors: () => ViolationType[]
    isEditorPluginsEnabled: () => boolean
    filesList: () => FileToModelValueType[]
    setFilesList: (filesList: FileToModelValueType[]) => void
}

export const ConversationsInputBlockComponent =
    BlockComponentFactory.createBlockComponent<ConversationsInputBlockProps>(props => {
        const {
            inputEditorBlockElement,
            editIndex: editIndexFn,
            setEditIndex,
            submitTurn,
            isTurnExhausted: isTurnExhaustedFn,
            isLoading,
            isResponseStreaming: isResponseStreamingFn,
            errors: errorsFn,
            isEditorPluginsEnabled: isEditorPluginsEnabledFn,
            userMessage: userMessageFn,
            filesList: filesListFn,
            setFilesList,
        } = props

        const userMessage = userMessageFn()
        const errors = errorsFn()
        const isEditorPluginsEnabled = isEditorPluginsEnabledFn()
        const isTurnExhausted = isTurnExhaustedFn()
        const isResponseStreaming = isResponseStreamingFn()
        const filesList = filesListFn()
        const editIndex = editIndexFn()

        const taskContext = useTaskContext()
        const taskId = taskContext.taskId
        const isReadOnly = taskContext.isReadOnly()

        const [isFileUploading, setIsFileUploading] = useState(false)

        const submitButtonDisabledReason = useMemo(() => {
            if (isTurnExhausted) {
                return 'You have exhausted your turns for this task'
            }
            if (isResponseStreaming) {
                return 'Please wait while the response is being generated'
            }
            if (isLoading) {
                return 'Please wait while we are processing your request'
            }
            if (isFileUploading) {
                return 'Please wait while we are uploading your file'
            }
            if (userMessage?.trim() === '') {
                return 'Input is required'
            }

            const filteredErrors = errors.filter(error => error.id !== 'attributes')
            if (filteredErrors.length > 0) {
                return filteredErrors[0].message
            }

            return ''
        }, [isTurnExhausted, isResponseStreaming, isLoading, isFileUploading, userMessage, errors])

        const buttonDisabledReason = useMemo(() => {
            if (editIndex !== -1) {
                return isEmpty(userMessage?.trim()) ? 'Input is required' : ''
            } else {
                return submitButtonDisabledReason
            }
        }, [editIndex, userMessage, submitButtonDisabledReason])

        return (
            <div
                className={
                    'conversations-v2-input-container' +
                    (isEditorPluginsEnabled ? ' plugins-enabled' : '')
                }
            >
                <EditorContainer
                    inputEditorBlockElement={inputEditorBlockElement}
                    filesList={filesList}
                    setFilesList={setFilesList}
                    buttonDisabledReason={buttonDisabledReason}
                    submitTurn={submitTurn}
                    setEditIndex={setEditIndex}
                    editIndex={editIndex}
                    taskId={taskId}
                    isVisible={editIndex === -1}
                    setIsFileUploading={setIsFileUploading}
                    isReadOnly={isReadOnly}
                />
            </div>
        )
    })
